import React from "react";

import { css } from "@emotion/css";
import { useTranslation } from "react-i18next";

import { useAPIErrorMessageParser } from "@/hooks/useAPIErrorMessageParser.ts";
import { useStyles } from "@/hooks/useStyles.ts";
import { Queries } from "@/queries/Queries.ts";
import { Theme } from "@/theme/CreateTheme.ts";
import { ScreenMax } from "@/theme/responsive.ts";
import { Button } from "@/uiKit/atoms/Button.tsx";
import { CardContent } from "@/uiKit/atoms/CardContent.tsx";
import { CardFooter } from "@/uiKit/atoms/CardFooter.tsx";
import { CardHeader } from "@/uiKit/atoms/CardHeader.tsx";
import { SideSheet } from "@/uiKit/atoms/SideSheet.tsx";
import { TextToast } from "@/uiKit/atoms/Toast.tsx";
import {
  NURSE_INCLUSION_FORM_ID,
  NursePreInclusionForm,
} from "@/uiKit/organisms/PatientCreationAndRenewal/NurseInclusion/NursePreInclusionForm.tsx";
import { termDateForCondition } from "@/uiKit/organisms/PatientCreationAndRenewal/termDateRules.ts";

export type NurseInclusionCreationSideSheetProps = {
  id: string;
  open: boolean;
  onClose: () => void;
  onSubmit: () => void;
};

export const NurseInclusionCreationSideSheet: React.FC<NurseInclusionCreationSideSheetProps> = ({
  id,
  open,
  onClose,
  onSubmit,
}) => {
  const { t } = useTranslation();
  const styles = useStyles(makeStyles);
  const errorParser = useAPIErrorMessageParser();
  const nursePreIncludePatient = Queries.practitioner.useCreateNurseInclusionSuggestion();

  return (
    <SideSheet id={id} open={open} onClose={onClose}>
      <CardHeader title={t("pages.patients.addNurseInclusion")} variant="contained" />
      <CardContent className={styles.content}>
        <NursePreInclusionForm
          onSubmit={data => {
            nursePreIncludePatient.mutate(
              {
                doctor_id: data.doctorId,
                first_name: data.givenName,
                last_name: data.familyName,
                email: data.email,
                phone_number: data.phone,
                helper_phone: data.helperPhone,
                birth_date: data.birthDate,
                condition_type: data.diabetesType,
                condition_medication_schema: data.insulinScheme,
                pump_model: data.pump,
                term_date: termDateForCondition(data.diabetesType, data.termDate),
                inclusion_reason: data.inclusionReason,
              },
              {
                onSuccess: () => {
                  TextToast.success(t("nurseInclusion.inclusionSent"));
                  onSubmit();
                },
                onError: error => {
                  const errorMessage = errorParser(error.data);
                  if (errorMessage) {
                    TextToast.error(errorMessage);
                  }
                },
              },
            );
          }}
        />
      </CardContent>
      <CardFooter>
        <Button
          variant="outlined"
          data-action="patient-creation.nurse-inclusion.cancel"
          id="nurse-inclusion-cancel"
          onClick={onClose}
        >
          {t("Annuler")}
        </Button>
        <Button
          form={NURSE_INCLUSION_FORM_ID}
          id="add-nurse-inclusion-patient"
          type="submit"
          variant="contained"
          data-action="patient-creation.nurse-inclusion.submit"
        >
          {t("Envoyer la proposition")}
        </Button>
      </CardFooter>
    </SideSheet>
  );
};

const makeStyles = (theme: Theme) => ({
  content: css({
    padding: theme.spacing(8, 24, 0, 24),
    [ScreenMax.SM]: {
      padding: theme.spacing(8, 12, 0, 12),
    },
  }),
});
